import superagentPromise from 'superagent-promise';
import _superagent from 'superagent';
import { notifyBackendRequestActivity } from './utils/backendActivity';

const superagent = superagentPromise(_superagent, global.Promise);

const API_ROOT = process.env.REACT_APP_API_URL;

// const encode = encodeURIComponent;
const responseBody = res => res.body;

const DEFAULT_ERROR_MESSAGE = 'Произошла ошибка. Попробуйте позже.';

const handleError = (err) => {
  // superagent error shape
  const status = err?.status ?? err?.response?.status;
  const body = err?.response?.body;
  const text = err?.response?.text;

  const extractMessage = () => {
    if (!body && text) return text;
    if (typeof body === 'string') return body;
    if (body && typeof body === 'object') {
      // Common fields from various backends
      if (typeof body.message === 'string' && body.message.trim()) return body.message;
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
      if (Array.isArray(body.errors) && body.errors.length) {
        const first = body.errors[0];
        if (typeof first === 'string') return first;
        if (first && typeof first.message === 'string') return first.message;
      }
      if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;
      if (typeof body.description === 'string' && body.description.trim()) return body.description;
    }
    return DEFAULT_ERROR_MESSAGE;
  };

  const msg = extractMessage();
  const isServerError = Number(status) === 500;

  // Return a friendly message instead of throwing, so UI can display it safely
  return isServerError ? DEFAULT_ERROR_MESSAGE : (msg || DEFAULT_ERROR_MESSAGE);
};

let token = null;
const tokenPlugin = req => {
  notifyBackendRequestActivity();
  if (token) {
    req.set('Authorization', `Bearer ${token}`);
  }
}

const buildQueryString = (params) =>
    Object.entries(params)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");

const requests = {
  del: url =>
      superagent.del(`${API_ROOT}${url}`).use(tokenPlugin).then(responseBody),
  get: (url, options) => {
      let req = superagent.get(`${API_ROOT}${url}`).use(tokenPlugin);
      if (options && typeof options === 'object') {
        req = req.query(options);
      }
      return req.then(responseBody);
    },
  put: (url, body) =>
      superagent.put(`${API_ROOT}${url}`, body).use(tokenPlugin).then(responseBody),
  post: (url, body) =>
      superagent.post(`${API_ROOT}${url}`, body).use(tokenPlugin).then(responseBody),
  from: (url, body) =>
      superagent.post(`${API_ROOT}${url}`, body).use(tokenPlugin).type('form').then(res => {
        res.body = JSON.parse(res.text);
        res.body.token = res.header['authorization'];
        return responseBody(res);
      }),
  download: url =>
      superagent.get(`${API_ROOT}${url}`).use(tokenPlugin).responseType('blob'),
  patch: (url, body) =>
      superagent.patch(`${API_ROOT}${url}`, body).use(tokenPlugin).then(responseBody),
  formData: (url, body, files) => {
    const req = superagent.post(`${API_ROOT}${url}`)
    for (const [key, value] of Object.entries(body)) {
      req.field(key, value);
    }
    files.forEach((file) => req.attach(file.name, file.binary))
    return req.set('enctype', 'multipart/form-data').use(tokenPlugin).then(responseBody)
  }
};

const Auth = {
  current: () =>
      requests.get('/protected/user'),
  login: (email, password) =>
      requests.from('/login', {  username: email, password }),
  register: (username, email, password) =>
      requests.post('/users', { user: { username, email, password } }),
  save: user =>
      requests.put('/user', { user }),
  changePassword: body =>
      requests.patch(`/protected/user/password`, body)
};

const PostalService = {
  all: () =>
      requests.get(`/protected/postal/service`),
  getAllForFilter: () =>
      requests.get(`/protected/postal/service/filter`),
  get: id =>
      requests.get(`/protected/postal/service/${id}`),
  setStatus: (id, status) =>
      requests.patch(`/protected/postal/service/${id}?status=${status}`)
};


const PostalRoute = {
  all: (page, pageSize, q) =>
      requests.get(`/protected/postal/routes?page=${page}&pageSize=${pageSize}${q}`),
  get: id =>
      requests.get(`/protected/postal/routes/${id}`),
  searchCities: (name) =>
      requests.get(`/protected/direction?name=${name}`),
  addRoute: (body) => 
      requests.post('/protected/postal/routes/add', body),
  editRoute: (id, status) =>
      requests.patch(`/protected/postal/routes/${id}?status=${status}`),
  uploadFile: (body) =>
      requests.post('/protected/postal/routes/file', body),
  downloadFile: () => 
      requests.download('/protected/postal/routes/file'),
};

const Orders = {
  all: (page = 0, pageSize = 10, q) =>
      requests.get(`/protected/orders?page=${page}&pageSize=${pageSize}${q}`),
  get: id =>
      requests.get(`/protected/order/${id}`),
  getFile: (orderNum, fileType) =>
      requests.download(`/protected/file?orderNum=${orderNum}&fileType=${fileType}`),
  getChildren: id =>
      requests.get(`/protected/order/${id}/children`),
  getExcel: (q) =>
      requests.download(`/protected/report/orders/excel/map?${q}`),
  update: (orderNum, body) =>
      requests.patch(`/protected/order/${orderNum}`, body),
  updateNote: (orderNum, note) =>
      requests.patch(`/protected/order/note/${orderNum}?note=${note}`),
  create: (body, orgId) =>
      requests.post(`/protected/order?orgId=${orgId}`, body),
  getEvents: (orderNum) => 
      requests.get(`/protected/order/${orderNum}/events`),
  getExtraAddress: (orderNum) => 
      requests.get(`/protected/order/${orderNum}/extraAddress`),
  findPtcUser: (ptcUser) =>
      requests.get(`/protected/report/orders/find-ptc-user?user=${ptcUser}`)
}

const CalculatedOrder = {
  create: (body, apiKey) => {
    const base = API_ROOT.replace(/\/admin$/, '');
    return superagent
      .post(`${base}/slq/api/v1/protected/calculated-route-order?apiKey=${apiKey}`, body)
      .use(tokenPlugin)
      .then(responseBody);
  }
}

const OrdersStatus = {
  all: () =>
      requests.get(`/protected/nsi/order/status`)
}

const ProductCategory = {
  all: (name='', code='', page=0, pageSize=10) =>
      requests.get(`/protected/tariff/category?page=${page}&pageSize=${pageSize}&name=${name}&code=${code}`),
  add: (body) =>
      requests.post(`/protected/tariff/category`, body),
  addList: (data, page=0, pageSize=10) =>
      requests.post(`/protected/tariff/category/file?page=${page}&pageSize=${pageSize}`, data),
}

const Offers = {
  get: (orderNum) =>
      requests.get(`/protected/offers?orderNum=${orderNum}`),
  change: (offerId, body) =>
      requests.patch(`/protected/offer/${offerId}`, body),
}

const OrderParcel = {
  get: (orderNum) =>
      requests.get(`/protected/parcels?orderNum=${orderNum}`)
}

const TaskQueue = {
  get: (orderNum) =>
      requests.get(`/protected/task_queue?orderNum=${orderNum}`),
  updateTaskQueue: (id, body) =>
      requests.patch(`/protected/task_queue/${id}`, body),
  all: () =>
      requests.get(`/protected/task_queue/type`),
  getById: (id) =>
      requests.get(`/protected/task_queue/${id}`),
  save: (body, files) =>
      requests.formData(`/protected/task_queue`, body, files)
}

const Report = {
  get: (category) =>
      requests.get(`/protected/reports/${category}`),
}

const Kato = {
  search: (name) =>
      requests.get(`/protected/nsi/kato?name=${name}`),
  searchGraph: (query, page = 0, pageSize = 10) => {
    const base = API_ROOT.replace(/\/admin$/, '');
    return superagent
      .get(`${base}/graph/kato`)
      .query({ page, pageSize, query })
      .use(tokenPlugin)
      .then(responseBody);
  }
}

const CrossDocking = {
  getRegistry: (page = 0, pageSize = 1000) =>
      requests.get('/protected/graph/cross-docking', { page, pageSize }),
  getDetail: (id) =>
      requests.get('/protected/graph/cross-docking-detail', { id }),
  getTariffGrid: (crossDockingPointIds = []) =>
      requests.get(`/protected/cross-docking-tariffs/grid`, { crossDockingPointIds }),
  createTariffRow: (body) =>
      requests.post(`/protected/cross-docking-tariffs/grid/rows`, body),
  createTariffBand: (body) =>
      requests.post(`/protected/cross-docking-tariffs/grid/bands`, body),
  patchTariffRows: (body) => {
      return requests
        .patch(`/protected/cross-docking-tariffs/grid/rows`, body)
  },
  deleteTariffRow: (crossDockingPointId, kato) => {
      return requests
        .del(`/protected/cross-docking-tariffs/grid/rows/${crossDockingPointId}/${kato}`)
  },
  importTariffPreview: (carrierType, file) =>
      requests.formData(
        `/protected/cross-docking-tariffs/import/preview?carrierType=${encodeURIComponent(carrierType)}`,
        {},
        [{ name: "file", binary: file }]
      ),
  importTariffCommit: (body) =>
      requests.post(`/protected/cross-docking-tariffs/import/commit`, body),
  setActiveBulk: (changes) =>
      requests.patch(`/protected/graph/cross-docking/active`, { changes }),
}

const Calculator = {
  calculate: (body, orgId) =>
      requests.post(`/protected/calculator?orgId=${orgId}`, body),
}

const CalculatorShoulders = {
  calculate: (props) => {
    const base = API_ROOT.replace(/\/admin$/, '');
    return superagent
      .get(`${base}/graph/buildAdvance`)
      .query({
        from: props.from,
        to: props.to,
        weight: props.weight,
        cargoValue: props.cargoValue,
        numPack: props.numPack,
        paidWeight: props.paidWeight,
        productCategoryCode: props.productCategoryCode,
        productName: props.productName,
        deliveryFromAddress: props.deliveryFromAddress,
        deliveryToAddress: props.deliveryToAddress,
        withPrp: props.withPrp,
        withPrd: props.withPrd,
        orgId: props.orgId,
        isAsc: props.isAsc,
        sortBy: props.sortBy,
      })
      .use(tokenPlugin)
      .then(responseBody);
  },
    calculateAsync: (props, apiKey) => {
        const base = API_ROOT.replace(/\/admin$/, '');
        return superagent
            .post(`${base}/slq/api/v1/protected/calculator/path`, {
                from: props.from,
                to: props.to,
                weight: props.weight,
                cargoValue: props.cargoValue,
                numPack: props.numPack,
                paidWeight: props.paidWeight,
                productCategoryCode: props.productCategoryCode,
                productName: props.productName,
                deliveryFromAddress: props.deliveryFromAddress,
                deliveryToAddress: props.deliveryToAddress,
                withPrp: props.withPrp,
                withPrd: props.withPrd,
                orgId: props.orgId,
                isAsc: props.isAsc,
                sortBy: props.sortBy,
            })
            .query({apiKey: apiKey})
            .use(tokenPlugin)
            .then(responseBody);
    },
    calculateAsyncByHash: (hashCodeRequest, apiKey) => {
        const base = API_ROOT.replace(/\/admin$/, '');
        const normalizedHash = encodeURIComponent(String(hashCodeRequest || "").replace(/^\/+/, ""));
        return superagent
            .get(`${base}/slq/api/v1/protected/calculator/path/${normalizedHash}`)
            .query({apiKey: apiKey})
            .use(tokenPlugin)
            .then(responseBody);
    },
    calculateAdditionalAsync: (hashCodeRequest, priceTo, daysCntTo, carriers, apiKey, page = 0, pageSize = 5) => {
        const base = API_ROOT.replace(/\/admin$/, '');
        return superagent
            .post(`${base}/slq/api/v1/protected/calculator/custom-results`, { hashCodeRequest, priceTo, daysCntTo, carriers })
            .query({apiKey: apiKey, page, pageSize})
            .use(tokenPlugin)
            .then(responseBody);
    },
}

const PsOrderNum = {
  add: (orderId, track) =>
    requests.post(`/protected/postal/track?orderId=${orderId}&track=${track}`, null),
  remove: (id) =>
      requests.del(`/protected/postal/track?id=${id}`),
  update: (id, track) =>
      requests.patch(`/protected/postal/track?id=${id}&track=${track}`, null)
}

const StreetAbbr = {
  all: () =>
      requests.get(`/protected/nsi/street_abbr`)
}

const Invoices = {
  get: (orderNum) =>
      requests.get(`/protected/order/${orderNum}/invoices`),
  save: (orderNum, body) =>
      requests.post(`/protected/order/${orderNum}/invoices`, body)
}

const ApiItems = {
  get: () =>
      requests.get(`/protected/payment/items`)
}

const Organizations = {
  all: () =>
    requests.get(`/protected/orgs`),
  clients: () => 
      requests.get('/protected/nsi/client')
}

const Carriers = {
  all: (apiKey) => {
     const base = API_ROOT.replace(/\/admin$/, '');
     return superagent.get(`${base}/slq/api/v1/protected/carriers`)
     .query({apiKey: apiKey})
            .use(tokenPlugin)
            .then(responseBody);
  }
}

const Lock = {
    lock: (componentName) =>
        requests.post(`/protected/ui/block`, {componentName}),
    unlock: (componentName) =>
        requests.post(`/protected/ui/unblock`, {componentName}),
}

const SlqUser = {
  all: (realmId, q, page, pageSize) =>
      requests.get(`/protected/slq_users?realmId=${realmId}&q=${q}&page=${page}&pageSize=${pageSize}`),
  get: (guid) =>
      requests.get(`/protected/slq_user/${guid}`),
  updateRequisites: (guid, body) =>
      requests.patch(`/protected/slq_user/${guid}/requisites`, body),
  getWallet: (guid) =>
      requests.get(`/protected/slq_user/${guid}/wallet`),
  getWalletHistory: (guid, page, pageSize) =>
      requests.get(`/protected/slq_user/${guid}/wallet_history?page=${page}&pageSize=${pageSize}`),
  allOptions: (q) =>
      requests.get(`/protected/slq_user/options?q=${q}`),
  getStatuses: () => 
      requests.get(`/protected/avr/status`),
  getAvrs: (data, itemsPerPage=1, page=0) => 
      requests.get(`/protected/avrs?userId=${data.userId}&itemsPerPage=${itemsPerPage}&page=${page}&status=${data.status}&avrNum=${data.avrNum}`),
  setStatus: (avrId) => 
      requests.post(`/protected/avr/status?avrId=${avrId}`),
}

const Tariff = {
  save: (data) =>
      requests.post(`/protected/tariff`, data),
  get: (userId) =>
      requests.get(`/protected/tariff/${userId}/disable`),
  disable: (userId) =>
      requests.put(`/protected/tariff/${userId}`),
  getTariff: (userId) =>
      requests.get(`/protected/tariff/${userId}`)
}

const CalcTariff = {
  getPostalServices: () =>
      requests.get('/protected/slq/calc/postalServices'),
  getUslugaPs: (psId) =>
      requests.get(`/protected/slq/calc/usluga/ps/${psId}`),
  savePsZone: (data) =>
      requests.post('/protected/slq/calc/psZone', data),
  deletePsZone: (tariffId) =>
      requests.del(`/protected/slq/calc/psZone/${tariffId}`),
  getPsZones: () =>
      requests.get('/protected/slq/calc/psZones'),
  uploadPsZoneKatoList: (tariffId, formDataWithCsvFile) =>
      requests.post(`/protected/slq/calc/upload-file/zone/${tariffId}`, formDataWithCsvFile),
  getPsZoneKatoList: (tariffId) =>
      requests.get(`/protected/slq/calc/psZone/${tariffId}/katoList`),
  getWeightList: (tariffId) =>
      requests.get(`/protected/slq/calc/psZone/${tariffId}/weightList`),
  editWeightList: (tariffId, data) =>
      requests.post(`/protected/slq/calc/psZone/${tariffId}/weight`, data),
  editZoneName: (tariffId, newName) =>
      requests.put(`/protected/slq/calc/psZone/${tariffId}/changeName?name=${newName}`, null),
  addNewKato: (tariffId, data) =>
      requests.post(`/protected/slq/calc/zoneKato/zone/${tariffId}`, data),
  deactivateKato: (tariffId, data) =>
      requests.post(`/protected/slq/calc/deactivate-zoneKato/zone/${tariffId}`, data),
  getTerminalAwayList: (code) =>
      requests.get(`/protected/slq/calc/take-away-from-terminal/?code=${code}`),
  editTerminalAwayList: (data) =>
      requests.post(`/protected/slq/calc/take-away-from-terminal`, data),
  getDeductionTTList: (code) =>
      requests.get(`/protected/slq/calc/take-away-terminals/?code=${code}`),
  editDeductionTTList: (data) =>
      requests.post(`/protected/slq/calc/take-away-terminals`, data),
}

const Terminals = {
    searchCity: (name) =>
        requests.get(`/protected/terminal/direction?name=${name}`),
    getTerminals: (id, kato, page = 0, pageSize = 10) =>
        requests.get(`/protected/terminal?id=${id}&kato=${kato}&page=${page}&pageSize=${pageSize}`),
    addTerminal: (data) => 
        requests.post(`/protected/terminal`, data),
    deleteTerminal: (id) => 
        requests.post(`/protected/terminal?id=${id}`),
    updateTerminal: (id, data) => 
        requests.post(`/protected/terminal?id=${id}`, data),
}

const Reports = {
    getStatus: () => 
        requests.get('/protected/report/async/orders/excel/map'),
    getProduceStatus: () => 
        requests.get('/protected/report/async/orders/excel/pending'),
    createReport: (params) => 
        requests.post(`/protected/report/async/orders/excel/map?${params}`),
    downloadReport: () => 
        requests.download('/protected/report/async/orders/excel/file'),
}

const StatusesMonitors = {
    getTimelineStatusses: () =>
        requests.get('/protected/timeline/status'),
    getAdminStatusses: () =>
        requests.get('/protected/timeline/status_admin'),
    getOrdersList: ({ status, statusAdmin, ps, orderNum }, page, pageSize) =>
        requests.get('/protected/timeline', {
            page,
            pageSize,
            status,
            statusAdmin,
            ps,
            orderNum
        }),
    getOrderHistory: (orderNum, page, pageSize) =>
        requests.get(`/protected/timeline/${orderNum}`, {
          page,
          pageSize
        }),
    setTimelineOrder: (body) =>
        requests.post('/protected/timeline', body),
    getTimelineByOrder: (orderNum) =>
        requests.get(`/protected/order/${orderNum}/timeline`)
}
const CatalogType = {
    listCatalogTypes: (code, page = 0, pageSize = 10) =>
        requests.get(`/protected/catalogType/${code}?page=${page}&pageSize=${pageSize}`),
    searchCatalogTypes: (code, name, page = 0, pageSize = 10) =>
        requests.get(`/protected/catalogType/${code}/search?name=${name}&page=${page}&pageSize=${pageSize}`),
    addCatalogType: (code, body) =>
        requests.post(`/protected/catalogType/${code}`, body),
    updateCatalogType: (code, id, body) =>
        requests.put(`/protected/catalogType/${code}?id=${id}`, body),
    deleteCatalogType: (code, id) =>
        requests.del(`/protected/catalogType/${code}?id=${id}`)
}

const AccountingDictionary = {
    listRecords: (code, page = 0, pageSize = 10) =>
        requests.get(`/protected/dictionary/${code}/page?page=${page}&pageSize=${pageSize}`),
    searchRecords: (code, params, page = 0, pageSize = 10) => {
        const query = buildQueryString({ ...params, page, pageSize });
        return requests.get(`/protected/dictionary/${code}/search?${query}`);
    },
    getById: (code, id) => requests.get(`/protected/dictionary/${code}/${id}`),
    addRecord: (code, upsertDto) =>
        requests.post(`/protected/dictionary/${code}`, upsertDto),
    updateRecord: (code, id, upsertDto) =>
        requests.put(`/protected/dictionary/${code}/${id}`, upsertDto),
    deleteRecord: (code, id) =>
        requests.del(`/protected/dictionary/${code}/${id}`)
}

const SQLPostalService = {
    getPostalServices: (page = 0, size = 10) =>
        requests.get('/protected/api/v1/ps-tariffs', { page, size }),
    getPostalService: (id) =>
        requests.get(`/protected/api/v1/ps-tariffs/${id}`),
    flagsPostalService: (id, data) =>
        requests.patch(`/protected/api/v1/ps-tariffs/${id}/flags`, data),
}

const Dashboard = {
    getCounts: () =>
        requests.get('/protected/admin-dashboard/counts'),
    getFailedTasks: (page = 0, size = 20) =>
        requests.get('/protected/admin-dashboard/fails', { page, size }),
    getNewOverdue: (page = 0, size = 20) =>
        requests.get('/protected/admin-dashboard/new/overdue', { page, size }),
    getAverageProcessingTime: () =>
        requests.get('/protected/admin-dashboard/averageProcessingSecondsByType'),
    getRabbitQueue: () =>
        requests.get('/protected/admin-dashboard/rabbit/queues'),
    syncOrderCount: () =>
        requests.get('/protected/admin-dashboard/order/count-sync'),
}

const DpdTimeout = {
    getServiceTimeouts: () =>
        requests.get('/protected/dpd/timeout'),
    updateServiceTimeout: (service, data) =>
        requests.patch(`/protected/dpd/timeout/${service}`, data),
    refreshServiceTimeouts: (server) =>
        requests.post(`/protected/dpd/timeout/refresh?server=${server}`),
}

const Users = {
    getUsers: (page = 0, size = 20, filters = {}) =>
        requests.get('/protected/users', { page, size, ...filters }),
    getUser: (id) =>
        requests.get(`/protected/users/${id}`),
    updateUser: (id, data) =>
        requests.patch(`/protected/users/${id}`, data),
    createUser: (data) =>
        requests.post('/protected/users', data),
    lock: (id) =>
        requests.patch(`/protected/users/${id}/block`),
    unlock: (id) =>
        requests.patch(`/protected/users/${id}/activate`),
    resetPassword: (id) =>
        requests.post(`/protected/users/${id}/password/reset`, {}),
}
const Public = {
    resetPassword: (data) =>
        requests.post('/public/password/reset', data),
}

const AuditLogs = {
    getAuditLogs: (page = 0, size = 20, filters = {}) =>
        requests.get('/protected/slq/logs', { page, size, ...filters }),
}

const SlqTariffs = {
    list: ({ page = 0, size = 20, active, platform, postalType, clientName } = {}) =>
        requests.get('/protected/slq-tariffs', { page, size, active, platform, postalType, clientName }),
    get: (id) =>
        requests.get(`/protected/slq-tariffs/${id}`),
    create: (data) =>
        requests.post('/protected/slq-tariffs', data),
    update: (id, data) =>
        requests.put(`/protected/slq-tariffs/${id}`, data),
    setActive: (id, active) =>
        requests.patch(`/protected/slq-tariffs/${id}/active?active=${active}`),
    remove: (id) =>
        requests.del(`/protected/slq-tariffs/${id}`),
}

const Payments = {
    getAllPayments: ({ q, transactionId, returned, startDate, endDate,  itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/payments/all', {
            q,
            transactionId,
            returned,
            startDate,
            endDate,
            itemsPerPage,
            page,
        }),
    // Download payment report as a file (blob) with headers preserved for filename extraction
    getPaymentReportFile: ({ q, startDate, endDate, transactionId } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/payments/report`).use(tokenPlugin);
        req = req.query({ q, startDate, endDate, transactionId });
        return req.responseType('blob'); // returns response, not just body
    },
    getAllAvrs: ({ q, startDate, endDate, statusId, avrNum, itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/avrs/all', {
            q,
            startDate,
            endDate,
            status: statusId,
            avrNum,
            itemsPerPage,
            page,
        }),
    getAllPtcAvrs: ({ q, startDate, endDate, status, avrNum, itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/ptc/avrs/all', {
            q,
            startDate,
            endDate,
            status,
            avrNum,
            itemsPerPage,
            page,
        }),
    // Download AVR report as a file (blob); supports filtering by statusId and avrNum
    getAvrReportFile: ({ q, startDate, endDate, statusId, avrNum } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/avrs/report`).use(tokenPlugin);
        req = req.query({ q, startDate, endDate, statusId, avrNum });
        return req.responseType('blob');
    },
    getPtcAvrReportFile: ({ q, startDate, endDate, statusId, avrNum } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/ptc/avrs/report`).use(tokenPlugin);
        req = req.query({ q, startDate, endDate, statusId, avrNum });
        return req.responseType('blob');
    },
    getJurAvrPdf: (avrId, userId) =>
        superagent.get(`${API_ROOT}/protected/avr/${avrId}/pdf`).query({ userId }).set('Accept', 'application/pdf').use(tokenPlugin).responseType('blob'),

    getPtcWithdrawsReportFile: ({ paymentId, iin, name, withdrawDateFrom, withdrawDateTo, amount, feeAmount, phone, email, contractNumber, contractDateFrom, contractDateTo, operationId, currency } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/ptc/withdraws/report`).use(tokenPlugin);
        req = req.query({
            paymentId,
            iin,
            name,
            withdrawDateFrom,
            withdrawDateTo,
            amount,
            feeAmount,
            phone,
            email,
            contractNumber,
            contractDateFrom,
            contractDateTo,
            operationId,
            currency
        });
        return req.responseType('blob');
    },
    getAllWithdraws: ({ paymentId, iin, name, withdrawDateFrom, withdrawDateTo, amount, feeAmount, phone, email, contractNumber, contractDateFrom, contractDateTo, operationId, currency, page = 0, itemsPerPage = 25 } = {}) =>
        requests.get('/protected/ptc/withdraws/all', {
            paymentId,
            iin,
            name,
            withdrawDateFrom,
            withdrawDateTo,
            amount,
            feeAmount,
            phone,
            email,
            contractNumber,
            contractDateFrom,
            contractDateTo,
            operationId,
            currency,
            page,
            itemsPerPage,
        }),
}

export default {
  Reports,
  Auth,
  PostalService,
  PostalRoute,
  Orders,
  OrdersStatus,
  ProductCategory,
  Offers,
  OrderParcel,
  TaskQueue,
  Report,
  Kato,
  Calculator,
  CalculatorShoulders,
  CalculatedOrder,
  PsOrderNum,
  StreetAbbr,
  Invoices,
  ApiItems,
  Organizations,
  Carriers,
  Lock,
  SlqUser,
  Tariff,
  Terminals,
  CalcTariff,
  StatusesMonitors,
  SQLPostalService,
  Dashboard,
  DpdTimeout,
  Users,
  Public,
  AuditLogs,
  SlqTariffs,
  Payments,
  CatalogType,
  AccountingDictionary,
  CrossDocking,
  setToken: _token => { token = _token; },
  handleError
};
